import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import { fetchTrackingSessions } from "../../redux/features/trackingSessions";
import { getEmployees } from "../../redux/features/common";
import { TrackingSession } from "../../types/model/trackingSessionTypes";
import { Employee } from "../../types/response/common";

const today = () => new Date().toISOString().split("T")[0];

function formatDuration(startedAt: string, endedAt?: string): string {
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const diffMs = Math.max(0, end - start);
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

function formatDateTime(iso?: string): { date: string; time: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
  };
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const STATUS_STYLES: Record<TrackingSession["status"], string> = {
  active: "bg-green-100 text-green-700 border border-green-300",
  paused: "bg-orange-100 text-orange-700 border border-orange-300",
  stopped: "bg-green-100 text-green-700 border border-green-300",
  auto_stopped: "bg-red-100 text-red-700 border border-red-300",
};

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-pink-100 text-pink-700",
  "bg-indigo-100 text-indigo-700",
  "bg-teal-100 text-teal-700",
];

function avatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const PAGE_SIZE = 5;

const TrackingSessionsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sessions, loading, error } = useSelector((state: RootState) => state.trackingSessions);
  const employeeList = useSelector((state: RootState) => state.common.employeeList);
  const userAccountId = useSelector((state: RootState) => state.user.user?.user_account_id);

  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TrackingSession["status"] | "">("");
  const [siteFilter, setSiteFilter] = useState("");
  const [page, setPage] = useState(1);

  // Fetch employees once on mount
  useEffect(() => {
    dispatch(getEmployees({ l_i_m_i_t: "500" }));
  }, [dispatch]);

  useEffect(() => {
    if (!userAccountId) return;
    dispatch(fetchTrackingSessions({ startDate, endDate, userAccountId }));
    setPage(1);
  }, [dispatch, startDate, endDate, userAccountId]);

  // Build lookup map: employee_id → Employee
  const employeeMap = useMemo(() => {
    const map: Record<string, Employee> = {};
    employeeList.forEach((emp) => { map[emp.employee_id] = emp; });
    return map;
  }, [employeeList]);

  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      if (statusFilter && s.status !== statusFilter) return false;
      if (siteFilter && s.site_id !== siteFilter) return false;
      if (employeeSearch) {
        const q = employeeSearch.toLowerCase();
        const emp = employeeMap[s.os_emp_account_id];
        const name = emp ? emp.full_name.toLowerCase() : "";
        if (
          !s.employeeId.toLowerCase().includes(q) &&
          !s.os_emp_account_id.toLowerCase().includes(q) &&
          !name.includes(q)
        )
          return false;
      }
      return true;
    });
  }, [sessions, statusFilter, siteFilter, employeeSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const summary = useMemo(
    () => ({
      total: sessions.length,
      active: sessions.filter((s) => s.status === "active").length,
      stopped: sessions.filter((s) => s.status === "stopped").length,
      auto_stopped: sessions.filter((s) => s.status === "auto_stopped").length,
    }),
    [sessions]
  );

  const allSites = useMemo(
    () => Array.from(new Set(sessions.map((s) => s.site_id).filter(Boolean) as string[])).sort(),
    [sessions]
  );

  const handleExport = () => {
    const rows = [
      ["Employee ID", "Started At", "Ended At", "Duration", "Status", "Stop Reason", "Site"],
      ...filtered.map((s) => [
        s.os_emp_account_id,
        s.startedAt,
        s.endedAt ?? "",
        formatDuration(s.startedAt, s.endedAt),
        s.status,
        s.stop_reason ?? "",
        s.site_id ?? "",
      ]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tracking-sessions-${startDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tracking Sessions</h1>
          <p className="text-sm text-gray-500 mt-0.5">Monitor location tracking data from Firestore</p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6 bg-white border border-gray-200 rounded-xl p-4">
        {/* Date range */}
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-[220px]">
          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="text-sm text-gray-700 outline-none border-none bg-transparent"
          />
          <span className="text-gray-400 text-sm">–</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="text-sm text-gray-700 outline-none border-none bg-transparent"
          />
        </div>

        {/* Employee filter */}
        <div className="relative">
          <select
            value={employeeSearch}
            onChange={(e) => { setEmployeeSearch(e.target.value); setPage(1); }}
            className="appearance-none border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 bg-white outline-none focus:border-blue-400 min-w-[160px]"
          >
            <option value="">All Employees</option>
            {Array.from(new Set(sessions.map((s) => s.os_emp_account_id))).sort().map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
          <svg className="w-4 h-4 text-gray-400 absolute right-2 top-2.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Site filter */}
        <div className="relative">
          <select
            value={siteFilter}
            onChange={(e) => { setSiteFilter(e.target.value); setPage(1); }}
            className="appearance-none border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 bg-white outline-none focus:border-blue-400 min-w-[140px]"
          >
            <option value="">All Sites</option>
            {allSites.map((site) => (
              <option key={site} value={site}>{site}</option>
            ))}
          </select>
          <svg className="w-4 h-4 text-gray-400 absolute right-2 top-2.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Status filter */}
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as TrackingSession["status"] | ""); setPage(1); }}
            className="appearance-none border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 bg-white outline-none focus:border-blue-400 min-w-[140px]"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="stopped">Stopped</option>
            <option value="auto_stopped">Auto-stopped</option>
          </select>
          <svg className="w-4 h-4 text-gray-400 absolute right-2 top-2.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white ml-auto min-w-[200px]">
          <input
            type="text"
            placeholder="Search employee..."
            value={employeeSearch}
            onChange={(e) => { setEmployeeSearch(e.target.value); setPage(1); }}
            className="text-sm text-gray-700 outline-none border-none bg-transparent flex-1"
          />
          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Total Sessions</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{summary.total}</p>
              <p className="text-xs text-gray-400 mt-1">tracking_sessions</p>
            </div>
            <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Active</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{summary.active}</p>
              <p className="text-xs text-gray-400 mt-1">status = active</p>
            </div>
            <div className="w-9 h-9 flex items-center justify-center">
              <span className="w-4 h-4 rounded-full bg-green-500 block" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Completed (stopped)</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{summary.stopped}</p>
              <p className="text-xs text-gray-400 mt-1">status = stopped</p>
            </div>
            <div className="w-9 h-9 flex items-center justify-center">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Auto-stopped</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{summary.auto_stopped}</p>
              <p className="text-xs text-gray-400 mt-1">status = auto_stopped</p>
            </div>
            <div className="w-9 h-9 flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {!userAccountId || loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-48 text-red-500 text-sm">{error}</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-5 py-3 font-semibold text-gray-700">
                    Employee
                    <div className="text-xs font-normal text-gray-400">employeeId / os_emp_account_id</div>
                  </th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-700">
                    Started At
                    <div className="text-xs font-normal text-gray-400">startedAt</div>
                  </th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-700">
                    Ended At
                    <div className="text-xs font-normal text-gray-400">endedAt</div>
                  </th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-700">Duration</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-700">
                    Status
                    <div className="text-xs font-normal text-gray-400">status / stop_reason</div>
                  </th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-700">
                    Site
                    <div className="text-xs font-normal text-gray-400">site_id</div>
                  </th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-gray-400 py-16">
                      No sessions found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  paginated.map((session) => {
                    const startDt = formatDateTime(session.startedAt);
                    const endDt = formatDateTime(session.endedAt);
                    const emp = employeeMap[session.os_emp_account_id];
                    const displayName = emp ? emp.full_name : session.os_emp_account_id;
                    const initials = getInitials(displayName);
                    const color = avatarColor(session.os_emp_account_id);

                    return (
                      <tr key={session.sessionId} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                        {/* Employee */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            {emp?.profile_image_url ? (
                              <img
                                src={emp.profile_image_url}
                                alt={displayName}
                                className="w-9 h-9 rounded-full object-cover shrink-0"
                              />
                            ) : (
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${color}`}>
                                {initials}
                              </div>
                            )}
                            <div>
                              <div className="font-semibold text-gray-900">{displayName}</div>
                              <div className="text-xs text-gray-400">{session.os_emp_account_id}</div>
                            </div>
                          </div>
                        </td>

                        {/* Started At */}
                        <td className="px-5 py-4 text-gray-700">
                          {startDt ? (
                            <>
                              <div className="font-medium">{startDt.time}</div>
                              <div className="text-xs text-gray-400">{startDt.date}</div>
                            </>
                          ) : "—"}
                        </td>

                        {/* Ended At */}
                        <td className="px-5 py-4 text-gray-700">
                          {endDt ? (
                            <>
                              <div className="font-medium">{endDt.time}</div>
                              <div className="text-xs text-gray-400">{endDt.date}</div>
                            </>
                          ) : (
                            <>
                              <div className="text-gray-400">—</div>
                              <div className="text-xs text-gray-400">ongoing</div>
                            </>
                          )}
                        </td>

                        {/* Duration */}
                        <td className="px-5 py-4 font-medium text-gray-700">
                          {formatDuration(session.startedAt, session.endedAt)}
                        </td>

                        {/* Status */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[session.status]}`}>
                              {session.status.replace("_", " ")}
                            </span>
                            {session.status === "active" && (
                              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            )}
                          </div>
                          {session.stop_reason && (
                            <div className="text-xs text-gray-400">{session.stop_reason}</div>
                          )}
                          {!session.stop_reason && session.status === "active" && (
                            <div className="text-xs text-gray-400">—</div>
                          )}
                        </td>

                        {/* Site */}
                        <td className="px-5 py-4 font-medium text-gray-700">
                          {session.site_id ?? "—"}
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-4">
                          <button
                            onClick={() => navigate(`/tracking-sessions/${session.sessionId}`)}
                            className="flex items-center gap-1 text-blue-600 font-medium text-sm hover:text-blue-800 transition-colors"
                          >
                            View Details
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Showing {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} to{" "}
                {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} sessions
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 flex items-center justify-center rounded border text-sm font-medium transition-colors ${
                        page === pageNum
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && (
                  <>
                    <span className="px-1 text-gray-400">...</span>
                    <button
                      onClick={() => setPage(totalPages)}
                      className={`w-8 h-8 flex items-center justify-center rounded border text-sm font-medium transition-colors ${
                        page === totalPages
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TrackingSessionsPage;
